// @flow
import React from "react";
import classNames from "classnames";
import withStyles from "elevate-ui/withStyles";
import { withGlobalContext } from "../common/GlobalContext";

import type { $Agent } from "flow/types";

import RenderNavItems from "./RenderNavItems";

type $Props = {
  agent: $Agent,
  classes: Object,
  headerNav: string,
  onToggleDropdown: Function,
  openDropdowns: Object,
};

const RenderNav = ({
  agent,
  classes,
  headerNav,
  onToggleDropdown,
  openDropdowns,
}: $Props) => (
  <nav
    className={classNames({
      [classes.navBar]: true,
      [classes.defaultFloat]: headerNav === "default",
      [classes.otherHeaderFloat]: headerNav !== "default",
      [classes.v2Headers]:
        headerNav === "threeColumnNavBar" ||
        headerNav === "officeLogoRightSideNavBar",
    })}
  >
    <div className={classes.navBarHeader}>
      <button
        className={classes.navBarToggle}
        type="button"
        onClick={(e) => onToggleDropdown(e, "navbar")}
        aria-expanded={openDropdowns && openDropdowns.navbar}
      >
        <span className={classes.srOnly}>Toggle navigation</span>
        <span className={classes.iconBar} />
        <span className={classes.iconBar} />
        <span className={classes.iconBar} />
      </button>
      {agent ? (
        <a className={classes.navBarBrand} href="/">
          <span>{agent.fullName}</span>
        </a>
      ) : null}
    </div>
    <div
      className={classNames(
        classes.mobileNavBar,
        openDropdowns.navbar && classes.mobileNavBarActive
      )}
    >
      <RenderNavItems />
    </div>
  </nav>
);

const styles = (theme) => {
  return {
    navBar: {
      marginBottom: 0,
      border: "1px solid transparent",
      borderColor: theme.colors.primary["500"],
      backgroundColor: theme.colors.primary["500"],
      fontSize: "1.2em",
      width: "100%",
      [theme.breakpoints(900)]: {
        width: "auto",
        maxHeight: "50px",
        borderRadius: theme.globalBorderRadius,
      },
    },
    navBarHeader: {
      display: "flex",
      flexFlow: "row nowrap",
      justifyContent: "space-between",
      alignItems: "center",
      [theme.breakpoints(900)]: {
        display: "block",
        float: "left",
      },
    },
    navBarToggle: {
      position: "relative",
      order: "2",
      padding: "9px 10px",
      marginTop: theme.spacing.unit * 0.5,
      marginRight: theme.spacing.unit,
      marginBottom: theme.spacing.unit * 0.75,
      backgroundColor: "transparent",
      backgroundImage: "none",
      border: `1px solid ${theme.colors.primaryFontColor}`,
      borderRadius: theme.globalBorderRadius,
      color: theme.colors.primaryFontColor,
      [theme.breakpoints(900)]: {
        display: "none",
      },
      "& $iconBar + $iconBar": {
        marginTop: theme.spacing.unit * 0.25,
      },
    },
    srOnly: {
      position: "absolute",
      width: "1px",
      height: "1px",
      padding: "0",
      margin: "-1px",
      overflow: "hidden",
      clip: "rect(0,0,0,0)",
      border: 0,
    },
    mobileNavBar: {
      display: "none",
      width: "100%",
      overflowY: "auto",
      textAlign: "center",
      paddingRight: theme.spacing.unit,
      paddingLeft: theme.spacing.unit,
      overflowX: "visible",
      borderTop: theme.borders["default"],
      boxShadow: "inset 0 1px 0 rgba(255,255,255,.1)",
      [theme.breakpoints(900)]: {
        display: "block",
        maxHeight: "50px",
        height: "auto",
        paddingBottom: "0",
        width: "auto",
        borderTop: "none",
        overflowY: "visible",
        boxShadow: "none",
      },
    },
    defaultFloat: {
      float: "none",
    },
    otherHeaderFloat: {
      float: "right",
    },
    mobileNavBarActive: {
      display: "block",
    },
    iconBar: {
      display: "block",
      width: theme.spacing.unit * 1.5,
      height: "2px",
      borderRadius: "1px",
      backgroundColor: theme.colors.primaryFontColor,
    },
    navBarBrand: {
      color: theme.colors.primaryFontColor,
      height: "auto",
      padding: "15px",
      float: "left",
      fontSize: "18px",
      lineHeight: "20px",
      textDecoration: "none",
      [theme.breakpoints(900)]: {
        display: "none",
      },
      "& span": {
        color: "inherit",
      },
      "&:hover": {
        textDecoration: "none",
      },
    },
    v2Headers: {
      [theme.breakpoints(900)]: {
        maxHeight: "none",
      },
      "& $mobileNavBar": {
        maxHeight: "none",
      },
    },
  };
};

export default withGlobalContext(withStyles(styles)(RenderNav));
